import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { voicePrefsApi, type VoicePrefsDTO } from '@/services/voicePrefsApi';

/**
 * Sync wake-word preferences between the per-user backend record and
 * the per-device localStorage `voice-settings-storage`.
 *
 *   - On EVERY post-auth mount: GET /users/me/voice-prefs and apply the
 *     server values into the local store. The server is the source of
 *     truth so every origin (app.maie.to + each tenant subdomain) renders
 *     1:1 — a toggle saved on one origin shows up on the next load of any
 *     other. The "refresh always off" race (a reload between a toggle and
 *     the debounced PUT letting a stale server `false` win) is held off by
 *     the `pagehide` keepalive flush below: the change persists before the
 *     reload's pull runs. (We used to gate this with a per-origin
 *     localStorage marker — which is exactly what kept each origin serving
 *     its own stale copy.)
 *   - On local toggle: debounced PUT so a flick-flick of the switch
 *     doesn't spam the backend, and the OTHER devices the doctor has
 *     open will pick the change up on their next boot.
 *   - On `pagehide`: flush any pending PUT synchronously via
 *     `fetch({ keepalive: true })` so a fast refresh after a toggle
 *     still ends up persisted server-side.
 *
 * Mounted once at MainLayout (post-auth root). No-op pre-auth.
 */
// 250ms is enough to coalesce the React.StrictMode double-fire and
// rapid radio-flips, but short enough that most real interactions
// flush before a deliberate refresh.
const PUSH_DEBOUNCE_MS = 250;

export function useVoicePrefsSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id);

  // Track last-pushed shape so an inbound hydrate doesn't immediately
  // PUT back the server's own values (would be benign, just wasteful).
  const lastPushedRef = useRef<string | null>(null);
  const hydratedForUserRef = useRef<string | null>(null);

  // Pull from the server once per authenticated mount. A fresh page load on
  // any origin re-pulls, so the server stays authoritative and every origin
  // renders 1:1. The per-mount ref keeps it to a single fetch; it resets on
  // unmount so the next page load re-pulls.
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      hydratedForUserRef.current = null;
      return;
    }
    if (hydratedForUserRef.current === userId) return;
    hydratedForUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        const prefs = await voicePrefsApi.get();
        if (cancelled) return;
        // Skip undefined / null fields — server may return a partially
        // populated record for users who've never saved anything, and
        // overwriting the local default with `undefined` makes the
        // settings radios render unchecked.
        const next: Partial<{
          enableWakeWord: boolean;
          wakeWordMode: 'always-listening' | 'push-to-activate';
          sttLanguage: 'en' | 'ar' | 'auto';
          defaultCaptureMode: 'visit' | 'dictate';
          preferRefinedForNote: boolean;
        }> = {};
        if (typeof prefs.wakeWordEnabled === 'boolean') {
          next.enableWakeWord = prefs.wakeWordEnabled;
        }
        if (
          prefs.wakeWordMode === 'always-listening' ||
          prefs.wakeWordMode === 'push-to-activate'
        ) {
          next.wakeWordMode = prefs.wakeWordMode;
        }
        if (
          prefs.sttLanguage === 'en' ||
          prefs.sttLanguage === 'ar' ||
          prefs.sttLanguage === 'auto'
        ) {
          next.sttLanguage = prefs.sttLanguage;
        }
        if (
          prefs.defaultCaptureMode === 'visit' ||
          prefs.defaultCaptureMode === 'dictate'
        ) {
          next.defaultCaptureMode = prefs.defaultCaptureMode;
        }
        if (typeof prefs.preferRefinedForNote === 'boolean') {
          next.preferRefinedForNote = prefs.preferRefinedForNote;
        }
        // Mark as already pushed so the subscribe-effect below doesn't
        // turn around and PUT identical values right after hydrate.
        lastPushedRef.current = JSON.stringify({
          wakeWordEnabled:
            next.enableWakeWord ??
            useVoiceStore.getState().settings.enableWakeWord,
          wakeWordMode:
            next.wakeWordMode ??
            useVoiceStore.getState().settings.wakeWordMode,
          sttLanguage:
            next.sttLanguage ??
            useVoiceStore.getState().settings.sttLanguage,
          defaultCaptureMode:
            next.defaultCaptureMode ??
            useVoiceStore.getState().settings.defaultCaptureMode,
          preferRefinedForNote:
            next.preferRefinedForNote ??
            useVoiceStore.getState().settings.preferRefinedForNote,
        });
        if (Object.keys(next).length > 0) {
          useVoiceStore.getState().updateSettings(next);
        }
      } catch {
        // Swallow — pre-existing localStorage value still drives the UI;
        // the next mount retries the pull.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  // Push local changes back to the server. Subscribe to the slice we
  // care about (not the whole store) so unrelated voice store updates
  // — recording status, audio level, etc. — don't fire the debounce.
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastSeen: {
      wakeWordEnabled: boolean;
      wakeWordMode: string;
      sttLanguage: string;
      defaultCaptureMode: string;
      preferRefinedForNote: boolean;
    } | null = null;
    let pendingSnapshot: {
      wakeWordEnabled: boolean;
      wakeWordMode: string;
      sttLanguage: string;
      defaultCaptureMode: string;
      preferRefinedForNote: boolean;
    } | null = null;
    let pendingSerialized: string | null = null;

    const flushPending = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (!pendingSnapshot || !pendingSerialized) return;
      const snapshot = pendingSnapshot;
      const serialized = pendingSerialized;
      pendingSnapshot = null;
      pendingSerialized = null;

      // Use fetch with keepalive so the request survives the page
      // unload. Axios doesn't expose keepalive cleanly, so we hit the
      // endpoint directly and reuse the access token from localStorage.
      const apiBase =
        (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
        'http://localhost:3000';
      const token = localStorage.getItem('accessToken');
      try {
        void fetch(`${apiBase.replace(/\/$/, '')}/users/me/voice-prefs`, {
          method: 'PUT',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(snapshot),
        });
        lastPushedRef.current = serialized;
      } catch {
        // keepalive fetches can't really be observed post-unload; best-effort.
      }
    };

    const unsub = useVoiceStore.subscribe((state) => {
      const snapshot = {
        wakeWordEnabled: state.settings.enableWakeWord,
        wakeWordMode: state.settings.wakeWordMode,
        sttLanguage: state.settings.sttLanguage,
        defaultCaptureMode: state.settings.defaultCaptureMode,
        preferRefinedForNote: state.settings.preferRefinedForNote,
      };
      const serialized = JSON.stringify(snapshot);
      // Skip the first emission (zustand fires immediately) and any
      // emission that didn't actually change our slice.
      if (lastSeen) {
        if (
          lastSeen.wakeWordEnabled === snapshot.wakeWordEnabled &&
          lastSeen.wakeWordMode === snapshot.wakeWordMode &&
          lastSeen.sttLanguage === snapshot.sttLanguage &&
          lastSeen.defaultCaptureMode === snapshot.defaultCaptureMode &&
          lastSeen.preferRefinedForNote === snapshot.preferRefinedForNote
        ) {
          return;
        }
      }
      lastSeen = snapshot;

      if (serialized === lastPushedRef.current) return;

      pendingSnapshot = snapshot;
      pendingSerialized = serialized;

      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        timer = null;
        const toSend = pendingSnapshot;
        const toSendSerialized = pendingSerialized;
        pendingSnapshot = null;
        pendingSerialized = null;
        if (!toSend || !toSendSerialized) return;
        try {
          await voicePrefsApi.put(toSend as Partial<VoicePrefsDTO>);
          lastPushedRef.current = toSendSerialized;
        } catch {
          // Best-effort; the next change attempt will retry.
        }
      }, PUSH_DEBOUNCE_MS);
    });

    // `pagehide` is the one event that fires reliably across desktop
    // refresh, tab-close, and mobile-Safari background — which is the
    // exact set of cases the user hits when they "refresh and the
    // toggle is gone". `beforeunload` is the conventional choice but
    // doesn't fire on bfcache restores in Safari.
    const handlePageHide = () => flushPending();
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [isAuthenticated, userId]);
}
